import { DEMO_LOGINS, ELoginAs } from '@aqx-core/common/api/auth';
import { ICompany } from '@aqx-core/common/api/config';
import { isSIT } from '@aqx-core/common/configs';
import { checkIsNative } from '@aqx-core/common/validators';

import { EThemeTemplate, TENANT_THEMES_MAP } from './constants';

export enum EDevicePlatform {
  ANDROID = 'android',
  IOS = 'ios',
  WEB = 'web'
}

/**
 * Resolves theme template for a tenant with environment awareness.
 * demo2 uses HANTEC in SIT/RELEASE_SIT, DEFAULT in UAT/PROD.
 */
export const getThemeTemplate = (tenantId: string | undefined, environment?: string): EThemeTemplate => {
  if (!tenantId) {
    return EThemeTemplate.DEFAULT;
  }

  if (tenantId === 'demo2') {
    return isSIT(environment) ? EThemeTemplate.HANTEC : EThemeTemplate.DEFAULT;
  }

  return TENANT_THEMES_MAP[tenantId] ?? EThemeTemplate.DEFAULT;
};

/**
 * Gets the platform-specific URL for native apps.
 * Returns null if the platform-specific URL is not available.
 */
const getNativePlatformUrl = (
  devicePlatform: EDevicePlatform | undefined,
  androidUrl: string | null | undefined,
  iosUrl: string | null | undefined
): string | null => {
  if (devicePlatform === EDevicePlatform.ANDROID) {
    return androidUrl ?? null;
  }
  if (devicePlatform === EDevicePlatform.IOS) {
    return iosUrl ?? null;
  }
  return null;
};

/**
 * Gets the registration URL based on the selected tab and device platform.
 * For native apps (Android/iOS), it will use the platform-specific URL only.
 * If the platform-specific URL is not available, it returns null to indicate
 * that the internal form should be used instead.
 * @param companyProfile - The company profile containing registration URLs
 * @param selectedTab - The selected login tab (LIVE, MT4_DEMO, etc.)
 * @param devicePlatform - Optional device platform (android/ios/web). If not provided, will auto-detect.
 */
export const getRegistrationUrl = (
  companyProfile: ICompany | null | undefined,
  selectedTab: ELoginAs | undefined,
  devicePlatform?: EDevicePlatform
): string | null => {
  if (!selectedTab) {
    return '';
  }

  const {
    companyRegistrationUrl,
    webtraderDemoRegistrationUrl,
    webtraderDemoRegistrationAndroidUrl,
    webtraderDemoRegistrationIosUrl,
    webtraderLiveRegistrationUrl,
    webtraderLiveRegistrationAndroidUrl,
    webtraderLiveRegistrationIosUrl
  } = companyProfile ?? {};

  // For CRM/LIVE tab, use companyRegistrationUrl (no platform-specific URLs for CRM)
  if (selectedTab === ELoginAs.LIVE) {
    return companyRegistrationUrl ?? null;
  }

  const isNative = checkIsNative();
  const isDemoTab = DEMO_LOGINS.includes(selectedTab);

  // For native apps, use platform-specific URL only (no fallback to generic URL)
  if (isNative) {
    return isDemoTab
      ? getNativePlatformUrl(devicePlatform, webtraderDemoRegistrationAndroidUrl, webtraderDemoRegistrationIosUrl)
      : getNativePlatformUrl(devicePlatform, webtraderLiveRegistrationAndroidUrl, webtraderLiveRegistrationIosUrl);
  }

  // For web, use generic URL
  return isDemoTab ? webtraderDemoRegistrationUrl ?? null : webtraderLiveRegistrationUrl ?? null;
};
